import React, { useState, Children, Fragment, lazy } from 'react';
import { motion } from 'framer-motion';
import { Check, Anchor, ArrowRight, Gift, CreditCard, Star, Lightbulb, Play } from 'lucide-react';
// ─── ANIMATION VARIANTS ──────────────────────────────────────────────────────
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
// ─── GLASS CARD STYLE — blur reducido para mejor rendimiento móvil ────────────
const glass = {
  background: 'rgba(10,10,10,0.65)',
  backdropFilter: 'blur(4px)'
};
const glassDark = {
  background: 'rgba(10,10,10,0.78)',
  backdropFilter: 'blur(4px)'
};
function SectionWrapper({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <section className={`py-10 px-4 sm:px-6 ${className}`} data-id="element-5">
      <div className="max-w-3xl mx-auto" data-id="element-6">{children}</div>
    </section>;
}
// ─── LAZY YOUTUBE — carga el iframe solo al hacer clic ───────────────────────
function LazyYouTube({
  videoId
}: {
  videoId: string;
}) {
  const [active, setActive] = useState(false);
  const thumb = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  return <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl border border-[#C9A84C]/25 cursor-pointer" style={{
    paddingBottom: '177.78%'
  }} onClick={() => setActive(true)} data-id="element-7">
      {active ? <iframe src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=1`} title="Hábitos Atómicos Espirituales" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute inset-0 w-full h-full" data-id="element-8" /> : <>
          <img src={thumb} alt="Ver video" className="absolute inset-0 w-full h-full object-cover" loading="lazy" data-id="element-9" />
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/30" data-id="element-10">
            <div className="w-16 h-16 bg-[#C9A84C] rounded-full flex items-center justify-center shadow-lg shadow-[#C9A84C]/40" data-id="element-11">
              <Play size={28} className="text-[#0F0F0F] ml-1" fill="#0F0F0F" data-id="element-12" />
            </div>
          </div>
        </>}
    </div>;
}
// ─── 1. HERO ─────────────────────────────────────────────────────────────────
function HeroSection() {
  return <section className="pt-8 pb-10 px-4 sm:px-6" data-id="element-13">
      <div className="max-w-3xl mx-auto text-center" data-id="element-14">
        <motion.div initial="hidden" animate="visible" variants={stagger} data-id="element-15">
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-[#C9A84C]/15 text-[#E8D48A] text-sm font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-8 border border-[#C9A84C]/30" data-id="element-16">
            <Anchor size={14} data-id="element-17" />
            Sistema espiritual de hábitos diarios
          </motion.div>

          <motion.h1 variants={fadeUp} className="font-serif text-4xl sm:text-5xl md:text-6xl text-white leading-tight mb-6 drop-shadow-lg" data-id="element-18">
            ¿Y si el problema
            <br data-id="element-19" />
            <span className="italic text-[#E8D48A]" data-id="element-20">nunca fuiste tú?</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-xl sm:text-2xl text-white/80 font-serif italic mb-8 drop-shadow max-w-2xl mx-auto leading-relaxed" data-id="element-21">
            Llevas años intentando cambiar con fuerza de voluntad. Nadie te dijo
            que eso nunca iba a funcionar.
          </motion.p>

          <motion.div variants={fadeUp} className="text-lg text-white/75 leading-relaxed max-w-2xl mx-auto mb-6 space-y-3" data-id="element-22">
            <p data-id="element-23">
              Hay personas que se levantan a las 5am, leen la Biblia, hacen
              ejercicio y terminan sus proyectos. Y hay personas que{' '}
              <em data-id="element-24">quieren</em> hacer todo eso… pero no pueden sostenerse más de
              una semana.
            </p>
            <p data-id="element-25">La diferencia no es fe. No es disciplina. No es carácter.</p>
            <p className="text-[#E8D48A] font-semibold text-xl font-serif" data-id="element-26">
              La diferencia es el sistema.
            </p>
          </motion.div>

          <motion.div variants={fadeUp} data-id="element-27">
            <a href="#cta" data-id="element-28">
              <button className="bg-[#C9A84C] hover:bg-[#9A7A2E] text-[#0F0F0F] font-bold text-lg px-8 py-4 rounded-xl transition-colors duration-200 inline-flex items-center gap-2 shadow-lg shadow-[#C9A84C]/20" data-id="element-29">
                Quiero el sistema ANCLA
                <ArrowRight size={20} data-id="element-30" />
              </button>
            </a>
          </motion.div>

          <motion.p variants={fadeUp} className="text-sm text-white/35 italic mt-4" data-id="element-31">
            "Si no cabe en tu peor día, no es un hábito."
          </motion.p>
        </motion.div>
      </div>
    </section>;
}
// ─── 2. VIDEO — lazy load ────────────────────────────────────────────────────
function VideoSection() {
  return <SectionWrapper data-id="element-32">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-60px'
    }} variants={stagger} data-id="element-33">
        <motion.p variants={fadeUp} className="text-center text-sm font-semibold tracking-widest uppercase text-[#C9A84C]/80 mb-4" data-id="element-34">
          ¿Cuántas veces has dicho: "Esta vez sí lo hago"?
        </motion.p>
        <motion.div variants={fadeUp} className="mx-auto" style={{
        maxWidth: 360
      }} data-id="element-35">
          <LazyYouTube videoId="u_XyS1t7Cho" data-id="element-36" />
        </motion.div>
      </motion.div>
    </SectionWrapper>;
}
// ─── 3. BLACK HOLE ───────────────────────────────────────────────────────────
function BlackHoleSection() {
  return <SectionWrapper data-id="element-37">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-80px'
    }} variants={stagger} data-id="element-38">
        <motion.div variants={fadeUp} className="text-center mb-6" data-id="element-39">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4 border border-[#C9A84C]/30" style={glass} data-id="element-40">
            <Lightbulb size={22} className="text-[#E8D48A]" data-id="element-41" />
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3 drop-shadow-lg" data-id="element-42">
            Lo que nadie te ha dicho
            <br data-id="element-43" />
            <span className="italic text-[#E8D48A]" data-id="element-44">sobre los hábitos</span>
          </h2>
        </motion.div>

        <motion.div variants={fadeUp} className="rounded-2xl p-5 mb-4 border border-[#C9A84C]/20" style={glass} data-id="element-45">
          <div className="flex items-start gap-3" data-id="element-46">
            <span className="text-2xl flex-shrink-0" data-id="element-47">🧠</span>
            <div data-id="element-48">
              <p className="font-serif text-base text-[#E8D48A] mb-1.5" data-id="element-49">
                El 95% de los intentos fallan en los primeros 10 días.
              </p>
              <p className="text-white/70 leading-relaxed text-sm" data-id="element-50">
                No porque la persona sea débil. Sino porque el cerebro humano
                está diseñado para conservar energía, no para cambiar. Cuando
                intentas cambiar con pura voluntad, estás peleando contra
                millones de años de evolución.
                <strong className="text-white" data-id="element-51">
                  {' '}
                  Nunca ibas a ganar esa batalla.
                </strong>
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="rounded-2xl p-5 mb-4 border border-[#C9A84C]/20" style={glass} data-id="element-52">
          <div className="flex items-start gap-3" data-id="element-53">
            <span className="text-2xl flex-shrink-0" data-id="element-54">📖</span>
            <div data-id="element-55">
              <p className="font-serif text-base text-[#E8D48A] mb-1.5" data-id="element-56">
                La Biblia no habla de "disciplina" como la entendemos hoy.
              </p>
              <p className="text-white/70 leading-relaxed text-sm" data-id="element-57">
                Habla de <em data-id="element-58">constancia</em>, de <em data-id="element-59">ritmo diario</em>, de{' '}
                <em data-id="element-60">pequeñas fidelidades</em>. "El que es fiel en lo poco,
                también lo será en lo mucho." (Lucas 16:10). Dios no te pide que
                seas perfecto. Te pide que seas{' '}
                <strong className="text-white" data-id="element-61">constante en lo pequeño.</strong>
              </p>
            </div>
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="rounded-2xl p-5 border border-[#C9A84C]/40" style={{
        background: 'rgba(201,168,76,0.08)',
        backdropFilter: 'blur(4px)'
      }} data-id="element-62">
          <div className="flex items-start gap-3" data-id="element-63">
            <span className="text-2xl flex-shrink-0" data-id="element-64">⚓</span>
            <div data-id="element-65">
              <p className="font-serif text-base text-[#E8D48A] mb-1.5" data-id="element-66">
                Un ancla no te hace avanzar. Te impide hundirte.
              </p>
              <p className="text-white/80 leading-relaxed text-sm" data-id="element-67">
                No necesitas motivación todos los días. Necesitas algo que te
                mantenga en su lugar cuando llegue la tormenta. Cuando estés
                cansado. Cuando todo falle. Eso es lo que construye este
                sistema:
                <strong className="text-[#E8D48A]" data-id="element-68">
                  {' '}
                  no un hábito perfecto, sino un ancla que no suelta.
                </strong>
              </p>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </SectionWrapper>;
}
// ─── 4. MIRROR ───────────────────────────────────────────────────────────────
function MirrorSection() {
  return <SectionWrapper data-id="element-69">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-80px'
    }} variants={stagger} data-id="element-70">
        <motion.div variants={fadeUp} className="text-center mb-6" data-id="element-71">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-2 drop-shadow-lg" data-id="element-72">
            Responde con honestidad
          </h2>
          <p className="text-white/55 italic text-base" data-id="element-73">
            Solo tres preguntas. Sin trampa.
          </p>
        </motion.div>

        {[{
        q: '¿Sientes que tienes más potencial del que estás usando… pero no sabes cómo activarlo?',
        a: 'Sí, lo siento'
      }, {
        q: '¿Has intentado cambiar hábitos más de tres veces este año… y sigues exactamente en el mismo lugar?',
        a: 'Sí, me ha pasado'
      }, {
        q: '¿Crees que si tuvieras el sistema correcto — uno que encaje en tu vida real — podrías ser constante?',
        a: 'Sí, creo que sí'
      }].map((item, i) => <motion.div key={i} variants={fadeUp} className="rounded-2xl p-5 mb-3 border border-white/10" style={glass} data-id="element-74">
            <p className="font-serif text-lg sm:text-xl text-white leading-relaxed mb-4" data-id="element-75">
              {item.q}
            </p>
            <div className="flex items-center gap-2 bg-[#C9A84C]/15 border border-[#C9A84C]/40 text-[#E8D48A] font-bold px-4 py-2 rounded-xl text-base w-fit" data-id="element-76">
              <Check size={16} data-id="element-77" />
              {item.a}
            </div>
          </motion.div>)}

        <motion.div variants={fadeUp} className="rounded-2xl p-6 text-center border border-[#C9A84C]/30 mt-3" style={{
        background: 'rgba(201,168,76,0.07)',
        backdropFilter: 'blur(4px)'
      }} data-id="element-78">
          <p className="font-serif text-xl sm:text-2xl text-white leading-relaxed mb-2" data-id="element-79">
            Entonces ya tienes la respuesta.
          </p>
          <p className="text-white/70 text-base leading-relaxed" data-id="element-80">
            No te falta fe. No te falta ganas. Te falta el sistema que Dios
            diseñó para personas como tú:{' '}
            <strong className="text-[#E8D48A]" data-id="element-81">el Método ANCLA.</strong>
          </p>
        </motion.div>
      </motion.div>
    </SectionWrapper>;
}
// ─── 5. ANCLA METHOD ─────────────────────────────────────────────────────────
const anclaItems = [{
  letter: 'A',
  title: 'Acción mínima',
  desc: '5 minutos (nunca cero). No se trata de hacer más. Se trata de no desaparecer.'
}, {
  letter: 'N',
  title: 'Nutriente espiritual',
  desc: 'Un ritual breve que conecta con Dios. La fe no se prueba en una emoción del domingo.'
}, {
  letter: 'C',
  title: 'Cue (señal visible)',
  desc: 'Algo físico que te dispara el hábito. Tu entorno trabaja para ti, no en tu contra.'
}, {
  letter: 'L',
  title: 'Loop',
  desc: 'Repetición simple: misma hora o mismo gatillo. La identidad se construye en la repetición.'
}, {
  letter: 'A',
  title: 'Auditoría sin culpa',
  desc: 'Tracking para ver la verdad sin castigarte. Datos, no drama.'
}];
function AnclaSection() {
  return <SectionWrapper data-id="element-82">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-80px'
    }} variants={stagger} data-id="element-83">
        <motion.div variants={fadeUp} className="text-center mb-8" data-id="element-84">
          <div className="inline-flex items-center justify-center w-12 h-12 bg-[#C9A84C]/15 rounded-full mb-4 border border-[#C9A84C]/30" data-id="element-85">
            <Anchor size={24} className="text-[#C9A84C]" data-id="element-86" />
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-3 drop-shadow-lg" data-id="element-87">
            El Método ANCLA
          </h2>
          <p className="text-white/60 text-base max-w-xl mx-auto" data-id="element-88">
            Cinco pasos que caben en cualquier día, incluso en el peor.
          </p>
        </motion.div>

        <motion.div variants={stagger} className="space-y-3 mb-8" data-id="element-89">
          {anclaItems.map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 rounded-2xl p-5 border border-[#C9A84C]/15" style={glass} data-id="element-90">
              <div className="flex-shrink-0 w-11 h-11 bg-[#0F0F0F] rounded-xl flex items-center justify-center border border-[#C9A84C]/40" data-id="element-91">
                <span className="font-serif text-xl font-bold text-[#C9A84C]" data-id="element-92">
                  {item.letter}
                </span>
              </div>
              <div data-id="element-93">
                <h3 className="font-serif text-lg text-white mb-0.5" data-id="element-94">
                  {item.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed" data-id="element-95">
                  {item.desc}
                </p>
              </div>
            </motion.div>)}
        </motion.div>

        <motion.div variants={fadeUp} className="rounded-2xl p-6 text-center border border-[#C9A84C]/25" style={glassDark} data-id="element-96">
          <p className="font-serif text-lg sm:text-xl text-white leading-relaxed" data-id="element-97">
            Este no es un método para "hacer más".
          </p>
          <p className="font-serif text-lg sm:text-xl text-white/70 mt-1.5 leading-relaxed" data-id="element-98">
            Es un método para{' '}
            <span className="text-[#E8D48A] italic" data-id="element-99">dejar de desaparecer.</span>
          </p>
        </motion.div>
      </motion.div>
    </SectionWrapper>;
}
// ─── 6. OFFER ────────────────────────────────────────────────────────────────
function OfferSection() {
  return <SectionWrapper data-id="element-100">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-80px'
    }} variants={stagger} data-id="element-101">
        <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl text-white text-center mb-2 drop-shadow-lg" data-id="element-102">
          ¿Qué recibes exactamente?
        </motion.h2>
        <motion.p variants={fadeUp} className="text-center text-white/50 mb-8" data-id="element-103">
          Todo lo que necesitas para ejecutar el método ANCLA desde hoy.
        </motion.p>

        <motion.div variants={fadeUp} className="max-w-md mx-auto mb-8" data-id="element-104">
          <div className="rounded-2xl overflow-hidden border border-[#C9A84C]/25" style={glassDark} data-id="element-105">
            <div className="px-5 py-3 border-b border-[#C9A84C]/20 flex items-center gap-3" style={{
            background: 'rgba(10,10,10,0.40)'
          }} data-id="element-106">
              <span className="text-2xl" data-id="element-107">📖</span>
              <span className="text-[#C9A84C] text-sm font-semibold tracking-wide uppercase" data-id="element-108">
                Ebook Digital
              </span>
            </div>
            <div className="p-5" data-id="element-109">
              <h3 className="font-serif text-lg text-white mb-0.5" data-id="element-110">
                Hábitos Atómicos Espirituales
              </h3>
              <p className="text-white/45 text-xs mb-4 italic" data-id="element-111">
                El sistema completo en tus manos
              </p>
              <ul className="space-y-2 mb-5" data-id="element-112">
                {['El sistema ANCLA completo paso a paso', 'Ejercicios cortos y aplicables desde el día 1', 'Checklists de implementación', 'Plan de 30 días con Dios', 'Protocolo de reinicio (para cuando fallas)'].map((f, j) => <li key={j} className="flex items-start gap-2 text-sm text-white/75" data-id="element-113">
                    <Check size={13} className="text-[#C9A84C] flex-shrink-0 mt-0.5" data-id="element-114" />
                    {f}
                  </li>)}
              </ul>
              <div className="border border-[#C9A84C]/30 rounded-xl p-3.5 mb-5" style={{
              background: 'rgba(201,168,76,0.08)'
            }} data-id="element-115">
                <div className="flex items-center gap-2 mb-2" data-id="element-116">
                  <Gift size={13} className="text-[#E8D48A] flex-shrink-0" data-id="element-117" />
                  <p className="text-[#E8D48A] text-xs font-bold tracking-wide uppercase" data-id="element-118">
                    + 3 bonos exclusivos al pagar con tarjeta
                  </p>
                  <CreditCard size={13} className="text-[#E8D48A] flex-shrink-0" data-id="element-119" />
                </div>
                <ul className="space-y-1.5" data-id="element-120">
                  {['Bono 1 — Incluido con tu compra', 'Bono 2 — Incluido con tu compra', 'Bono 3 — Incluido con tu compra'].map((bono, j) => <li key={j} className="flex items-center gap-2 text-white/65 text-sm" data-id="element-121">
                      <Check size={11} className="text-[#C9A84C] flex-shrink-0" data-id="element-122" />
                      🎁 {bono}
                    </li>)}
                </ul>
              </div>
              <a href="#cta" className="block" data-id="element-123">
                <button className="w-full bg-[#C9A84C] hover:bg-[#9A7A2E] text-[#0F0F0F] font-bold text-base px-6 py-3.5 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2" data-id="element-124">
                  Quiero mi sistema ANCLA
                  <ArrowRight size={18} data-id="element-125" />
                </button>
              </a>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </SectionWrapper>;
}
// ─── 7. TESTIMONIALS ─────────────────────────────────────────────────────────
function StarRating({
  count = 5
}: {
  count?: number;
}) {
  return <div className="flex gap-0.5" data-id="element-126">
      {Array.from({
      length: count
    }).map((_, i) => <Star key={i} size={14} className="text-[#C9A84C] fill-[#C9A84C]" data-id="element-127" />)}
    </div>;
}
const testimonials = [{
  src: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face',
  name: 'Roberto M., 52 años',
  city: 'Monterrey, México',
  text: '"Llevaba 4 años intentando tener un tiempo devocional constante. Con el método ANCLA lo logré en 3 semanas. No porque sea más disciplinado, sino porque finalmente tengo un sistema que cabe en mi día real."'
}, {
  src: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face',
  name: 'Carmen L., 47 años',
  city: 'Ciudad de México',
  text: '"Lo que más me impactó fue el protocolo de reinicio. Antes cuando fallaba un día, lo abandonaba todo. Ahora sé exactamente qué hacer cuando me caigo. Eso cambió todo para mí."'
}, {
  src: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face',
  name: 'Arturo V., 58 años',
  city: 'Guadalajara, México',
  text: '"Soy empresario y pensé que ya lo sabía todo sobre productividad. Este libro me enseñó algo que ningún curso de negocios me había dicho: que sin un ancla espiritual, todo lo demás se cae. Transformador."'
}, {
  src: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&h=80&fit=crop&crop=face',
  name: 'Patricia R., 41 años',
  city: 'Puebla, México',
  text: '"Mamá de tres hijos, trabajo tiempo completo. Siempre decía que no tenía tiempo. El sistema ANCLA me demostró que 5 minutos al día son suficientes para empezar a cambiar. Ya van 45 días sin fallar."'
}];
function TestimonialsSection() {
  return <SectionWrapper data-id="element-128">
      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-80px'
    }} variants={stagger} data-id="element-129">
        <motion.div variants={fadeUp} className="text-center mb-8" data-id="element-130">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-2 drop-shadow-lg" data-id="element-131">
            Lo que dicen quienes
            <br data-id="element-132" />
            <span className="italic text-[#E8D48A]" data-id="element-133">ya lo están viviendo</span>
          </h2>
          <p className="text-white/50 text-sm" data-id="element-134">
            Personas reales. Resultados reales.
          </p>
        </motion.div>

        <motion.div variants={stagger} className="space-y-4" data-id="element-135">
          {testimonials.map((t, i) => <motion.div key={i} variants={fadeUp} className="rounded-2xl p-5 border border-[#C9A84C]/15" style={glass} data-id="element-136">
              <div className="flex items-start gap-3" data-id="element-137">
                <img src={t.src} alt={t.name} width={44} height={44} loading="lazy" decoding="async" className="w-11 h-11 rounded-full object-cover border-2 border-[#C9A84C]/30 flex-shrink-0" data-id="element-138" />
                <div className="flex-1" data-id="element-139">
                  <div className="flex items-center justify-between mb-0.5" data-id="element-140">
                    <p className="font-bold text-white text-sm" data-id="element-141">{t.name}</p>
                    <StarRating data-id="element-142" />
                  </div>
                  <p className="text-white/45 text-xs mb-2" data-id="element-143">{t.city}</p>
                  <p className="text-white/80 text-sm leading-relaxed italic" data-id="element-144">
                    {t.text}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        <motion.div variants={fadeUp} className="mt-5 rounded-2xl p-5 text-center border border-[#C9A84C]/20" style={{
        background: 'rgba(201,168,76,0.07)',
        backdropFilter: 'blur(4px)'
      }} data-id="element-145">
          <div className="flex items-center justify-center gap-1 mb-1" data-id="element-146">
            <StarRating count={5} data-id="element-147" />
          </div>
          <p className="text-[#E8D48A] font-bold text-base mb-0.5" data-id="element-148">
            4.9 / 5 estrellas
          </p>
          <p className="text-white/50 text-sm" data-id="element-149">Basado en más de 200 lectores</p>
        </motion.div>
      </motion.div>
    </SectionWrapper>;
}
// ─── 8. FINAL CTA ─────────────────────────────────────────────────────────────
function FinalCTASection() {
  return <section id="cta" className="py-10 px-4 sm:px-6" data-id="element-150">
      <div className="max-w-2xl mx-auto text-center" data-id="element-151">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={stagger} data-id="element-152">
          <motion.div variants={fadeUp} className="inline-flex items-center justify-center w-12 h-12 bg-[#C9A84C]/15 rounded-full mb-6 border border-[#C9A84C]/30" data-id="element-153">
            <Anchor size={24} className="text-[#C9A84C]" data-id="element-154" />
          </motion.div>

          <motion.h2 variants={fadeUp} className="font-serif text-3xl sm:text-4xl md:text-5xl text-white leading-tight mb-4 drop-shadow-lg" data-id="element-155">
            Ya sabes lo que necesitas.
            <br data-id="element-156" />
            <span className="italic text-[#C9A84C]" data-id="element-157">
              El siguiente paso es tuyo.
            </span>
          </motion.h2>

          <motion.p variants={fadeUp} className="text-white/65 text-base mb-6 leading-relaxed max-w-xl mx-auto" data-id="element-158">
            Hoy puedes seguir esperando el momento perfecto. O puedes empezar
            con 5 minutos y un sistema que no te suelta.
          </motion.p>

          <motion.div variants={fadeUp} className="rounded-2xl p-5 mb-6 text-left border border-[#C9A84C]/20" style={glass} data-id="element-159">
            <p className="text-[#E8D48A] text-xs font-bold tracking-widest uppercase mb-3" data-id="element-160">
              Incluye todo esto:
            </p>
            <ul className="space-y-2" data-id="element-161">
              {['📖 Ebook completo — Hábitos Atómicos Espirituales', '⚓ El Método ANCLA paso a paso', '🔄 Protocolo de reinicio para cuando fallas', '📅 Plan de 30 días con Dios', '🎁 3 bonos exclusivos al pagar con tarjeta'].map((item, i) => <li key={i} className="flex items-center gap-3 text-white/80 text-sm" data-id="element-162">
                  <Check size={13} className="text-[#C9A84C] flex-shrink-0" data-id="element-163" />
                  {item}
                </li>)}
            </ul>
          </motion.div>

          <motion.div variants={fadeUp} className="mb-5" data-id="element-164">
            <a href="https://pay.hotmart.com/S104255087F?checkoutMode=10&bid=1772481499817" target="_blank" rel="noopener noreferrer" onClick={() => {
            if (typeof window !== 'undefined' && (window as any).fbq) {
              ;
              (window as any).fbq('track', 'InitiateCheckout');
            }
          }} className="w-full sm:w-auto bg-[#C9A84C] hover:bg-[#9A7A2E] text-[#0F0F0F] font-bold text-xl px-12 py-5 rounded-xl transition-colors duration-200 inline-flex items-center justify-center gap-3 shadow-lg shadow-[#C9A84C]/20" data-id="element-165">
              COMPRAR AHORA
              <ArrowRight size={22} data-id="element-166" />
            </a>
          </motion.div>

          <motion.p variants={fadeUp} className="text-white/30 italic text-sm" data-id="element-167">
            "Si no cabe en tu peor día, no es un hábito."
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 pt-6 border-t border-white/8" data-id="element-168">
            <p className="font-serif text-white/30 text-sm" data-id="element-169">
              Menos promesas. Más obediencia práctica.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}
// ─── PAGE ────────────────────────────────────────────────────────────────────
export function SalesPage() {
  return <div className="page-bg min-h-screen" data-id="element-170">
      <HeroSection data-id="element-171" />
      <VideoSection data-id="element-172" />
      <BlackHoleSection data-id="element-173" />
      <MirrorSection data-id="element-174" />
      <AnclaSection data-id="element-175" />
      <OfferSection data-id="element-176" />
      <TestimonialsSection data-id="element-177" />
      <FinalCTASection data-id="element-178" />
    </div>;
}